import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './viewer.css';

const { Autodesk } = window;

// ─────────────────────────────────────────────────────────────
// Module-level singleton — survives re-renders and re-mounts.
// Autodesk.Viewing.Initializer must only be called once per page.
// ─────────────────────────────────────────────────────────────
let runtimeInitialized = false;
let runtimeReadyPromise = null;

const initializeRuntime = (options) => {
  if (!runtimeReadyPromise) {
    runtimeReadyPromise = new Promise((resolve) => {
      Autodesk.Viewing.Initializer(options, () => {
        runtimeInitialized = true;
        console.log('[Viewer] Autodesk runtime initialized');
        resolve();
      });
    });
  }
  return runtimeReadyPromise;
};

// ─────────────────────────────────────────────────────────────
// Viewer
//
// Props:
//   urn         — URL-safe model URN (required)
//   getToken    — (onTokenReady: (token: string, ttl: number) => void) => void
//                 Called by the APS SDK whenever it needs a (fresh) token.
//                 The parent supplies a stable ref-backed callback so APS
//                 always receives the latest token without the viewer being
//                 torn down and rebuilt on every rotation.
//   onViewerReady / onCameraChange / onSelectionChange — optional callbacks
//
// Token lifecycle
// ───────────────
// The viewer is created once and kept alive for the session.  The APS SDK
// calls getToken proactively before the token expires; because getToken is
// backed by a ref in the parent (InteractiveViewerModal) it always resolves
// to the current live token without causing a re-mount.
// ─────────────────────────────────────────────────────────────
const Viewer = (props) => {
  const { urn, getToken, onViewerReady, onCameraChange, onSelectionChange } = props;

  const containerRef = useRef(null);
  const viewerRef    = useRef(null);

  // ── Keep a ref to getToken so the APS getAccessToken closure ──
  // always delegates to the latest version of the callback without
  // requiring a viewer restart.  getToken itself may be re-created
  // by the parent when the underlying token string rotates.
  const getTokenRef = useRef(getToken);
  useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

  useEffect(() => {
    if (!containerRef.current || !urn) return;

    let cancelled = false; // guard against unmount during async init

    const options = {
      env: 'AutodeskProduction',
      api: 'derivativeV2',
      // Delegate to the ref so APS always invokes the latest getter.
      // This is the single point where the token reaches the SDK —
      // no direct token string ever appears in this component.
      getAccessToken: (onTokenReady) => getTokenRef.current(onTokenReady),
    };

    initializeRuntime(options).then(() => {
      // Component may have unmounted while waiting for runtime
      if (cancelled || !containerRef.current) return;

      // If a viewer already exists on this container, reuse it
      if (viewerRef.current) return;

      const viewer = new Autodesk.Viewing.GuiViewer3D(containerRef.current);

      const startedCode = viewer.start();
      if (startedCode > 0) {
        console.error('[Viewer] Failed to start Autodesk Viewer. Code:', startedCode);
        return;
      }

      viewerRef.current = viewer;
      console.log('[Viewer] Viewer started, calling onViewerReady');

      // ── Notify parent BEFORE loading the document ────────────
      // Parent registers GEOMETRY_LOADED_EVENT here, which fires
      // later when loadDocumentNode completes — no race condition.
      if (onViewerReady) {
        onViewerReady(viewer);
      }

      // ── Wire up optional callbacks ───────────────────────────
      if (onCameraChange) {
        viewer.addEventListener(
          Autodesk.Viewing.CAMERA_CHANGE_EVENT,
          () => onCameraChange({ viewer, camera: viewer.getCamera() }),
        );
      }

      if (onSelectionChange) {
        viewer.addEventListener(
          Autodesk.Viewing.SELECTION_CHANGED_EVENT,
          () => onSelectionChange(viewer.getSelection()),
        );
      }

      // ── Load the model ───────────────────────────────────────
      const documentId = `urn:${urn}`;
      Autodesk.Viewing.Document.load(
        documentId,
        (doc) => {
          if (cancelled) return;
          const defaultModel = doc.getRoot().getDefaultGeometry();
          viewer.loadDocumentNode(doc, defaultModel);
        },
        (err) => console.error('[Viewer] Failed to load document:', err),
      );
    });

    return () => {
      cancelled = true;
      if (viewerRef.current) {
        viewerRef.current.finish();
        viewerRef.current = null;
      }
    };
  }, [urn]); // ← getToken deliberately omitted: rotations are handled via
             //   getTokenRef so the viewer never tears down on token refresh.

  return <div ref={containerRef} id="viewer-container" />;
};

Viewer.propTypes = {
  urn:              PropTypes.string.isRequired,
  getToken:         PropTypes.func.isRequired,   // (onTokenReady) => void
  onViewerReady:    PropTypes.func,
  onCameraChange:   PropTypes.func,
  onSelectionChange: PropTypes.func,
};

export default Viewer;